/**
 * Where a contract's post-signing questionnaire stands (Trello #231, item 8 follow-up; Haim,
 * 2026-10-22: "in the sending ui and ux it should be clear that the שאלון מוכר will be sent").
 *
 * The statutory owner questionnaire (regulation 7(ד): "שאלון מוכר", for a rental "שאלון בעל זכות
 * בנכס") rides on the seller's / landlord's ORDER only. A template carries it when one of its listings
 * blocks has `sellerQuestionnaireEnabled && restrictToSingleListing`; the send then puts
 * `sellerQuestionnaire` on THAT document. It is a STEP of that contract, never a document of its own:
 * the client signs the contract (and confirms the addresses, on a hidden-addresses send) and right
 * after, on the same link, fills in and signs the questionnaire. The document stays `sent` / `partial`
 * until then, and its signer keeps `viewed` — so neither status ever said "signed, the questionnaire
 * is still open".
 *
 * ONE rule for every screen that shows it — the send form, both prepared dialogs, the automatic-send
 * confirmation, the realtor's own WhatsApp share, the sent list (own and office), the client card's
 * חתימות tab, the property card and the document panel — so no two of them can disagree:
 *   none        the document carries no questionnaire step: a contract without one; a questionnaire on
 *               its own ("שאלון בלבד", `documentKind: 'ownerQuestionnaire'`, which IS the questionnaire
 *               and has its own labels — never labelled twice); a draft; a declined or expired document.
 *   notStarted  the contract is not signed yet: the questionnaire comes right after it.
 *   awaiting    the contract is signed (main + addresses addendum, when it has one: the server serves
 *               the questionnaire only then) and the questionnaire is still open.
 *   done        filled in and signed (a completed document always is).
 */
import type { ListingListedFor, SignatureDocumentKind } from "../@types/sharedSchema";
import { isQuestionnaireOnlyDocument } from "../@types/sharedSchema";

export type QuestionnaireStep = "none" | "notStarted" | "awaiting" | "done";

/** A signer's dates as the owner's document (ISO strings) or the office projection carries them. */
export type QuestionnaireStepSigner = {
  id?: string;
  phone?: string;
  status?: string;
  mainSignedAt?: string | Date;
  addendumSignedAt?: string | Date;
  questionnaireSignedAt?: string | Date;
};

/**
 * The parts of a document the step is read from: the owner's own `SignatureDocument`
 * (`sellerQuestionnaire`) or a colleague's office row (`hasSellerQuestionnaire`,
 * `sellerQuestionnaireListedFor`) alike.
 */
export type QuestionnaireStepDocument = {
  status?: string;
  documentKind?: SignatureDocumentKind;
  /** A hidden-addresses send: the addresses addendum is signed between the contract and the questionnaire. */
  hideAddresses?: boolean;
  sellerQuestionnaire?: { listedFor?: ListingListedFor } | null;
  hasSellerQuestionnaire?: boolean;
  sellerQuestionnaireListedFor?: ListingListedFor;
  signers?: QuestionnaireStepSigner[];
};

/**
 * A TEMPLATE brings the questionnaire when one of its listings blocks opts in AND is restricted to one
 * property (the questionnaire is about one property). What the send form attaches it by.
 */
export const templateCarriesQuestionnaire = (
  template: { listingsBlocks?: Array<{ sellerQuestionnaireEnabled?: boolean; restrictToSingleListing?: boolean }> } | null | undefined
): boolean => !!template?.listingsBlocks?.some((block) => block.sellerQuestionnaireEnabled && block.restrictToSingleListing);

/** The contract carries the questionnaire as a step after its signature. A questionnaire on its own does not: it IS one. */
export const carriesQuestionnaireStep = (doc: QuestionnaireStepDocument | null | undefined): boolean =>
  !!doc && !isQuestionnaireOnlyDocument(doc) && (!!doc.sellerQuestionnaire || !!doc.hasSellerQuestionnaire);

/** The property type the questionnaire is named by: 'rent' is "שאלון בעל זכות בנכס", anything else "שאלון מוכר". */
export const questionnaireListedForOf = (doc: QuestionnaireStepDocument): ListingListedFor | undefined =>
  doc.sellerQuestionnaire?.listedFor ?? doc.sellerQuestionnaireListedFor;

/**
 * One signer of a contract that carries the questionnaire: done once they filled and signed it;
 * awaiting once the contract is signed — and, on a hidden-addresses send, its addresses confirmed
 * (the server refuses the questionnaire before that); not started before.
 */
export const signerQuestionnaireStep = (
  signer: QuestionnaireStepSigner,
  hasAddendum: boolean
): Exclude<QuestionnaireStep, "none"> => {
  if (signer.questionnaireSignedAt) return "done";
  const contractSigned = !!signer.mainSignedAt && (!hasAddendum || !!signer.addendumSignedAt);
  return contractSigned ? "awaiting" : "notStarted";
};

/**
 * The document's questionnaire step, over its signers who have not declined: awaiting while ANY of them
 * signed the contract and has the questionnaire open (what the realtor must not miss), done once every
 * one of them filled it, else not started. A completed document is done; a draft, a declined and an
 * expired one have no step to show.
 */
export const questionnaireStepOf = (doc: QuestionnaireStepDocument | null | undefined): QuestionnaireStep => {
  if (!doc || !carriesQuestionnaireStep(doc)) return "none";
  if (doc.status === "completed") return "done";
  if (doc.status === "draft" || doc.status === "declined" || doc.status === "expired") return "none";
  const steps = (doc.signers ?? [])
    .filter((signer) => signer.status !== "declined")
    .map((signer) => signerQuestionnaireStep(signer, !!doc.hideAddresses));
  if (steps.includes("awaiting")) return "awaiting";
  if (steps.length > 0 && steps.every((step) => step === "done")) return "done";
  return "notStarted";
};

/**
 * What the lists show beside the document's status, or nothing. "Done" on a completed document says
 * nothing "הושלם" does not already say, so the chip shows only while the document is still out.
 */
export const questionnaireChipStep = (
  doc: QuestionnaireStepDocument | null | undefined
): Exclude<QuestionnaireStep, "none"> | null => {
  const step = questionnaireStepOf(doc);
  if (step === "none" || (step === "done" && doc?.status === "completed")) return null;
  return step;
};

// ---------------------------------------------------------------------------------------------------
// A SET of contracts (one bundle preparation, e.g. a seller's order + exclusivity) goes out as ONE
// message with ONE link, and only the order carries the questionnaire. These say, for a message about
// any contract of a set, whether — and after which contract — the client meets it.
// ---------------------------------------------------------------------------------------------------

/** A document as the set rule reads it (the owner's own documents: ISO dates). */
export type SetMemberDocument = QuestionnaireStepDocument & {
  id: string;
  bundleId?: string;
  createdTime?: string;
  archivedAt?: string;
  templateNameSnapshot?: string;
  templateCustomerFacingNameSnapshot?: string;
  signers: QuestionnaireStepSigner[];
};

/** The server's `SET_WINDOW_MS` (signatureSetSend.service.ts): one preparation's documents are minutes apart at most. */
export const SIGNATURE_SET_WINDOW_MS = 10 * 60 * 1000;

const timeOf = (value?: string): number => (value ? new Date(value).getTime() : NaN);

/**
 * The contracts of `anchor`'s set among `documents`, anchor included, in the order they were sent: the
 * server's set rule (`isSetMember`), for display — the same bundle, the same client phone (`phone`,
 * default the anchor's first signer's), created within ten minutes of the anchor. Outside a set, the
 * anchor alone.
 */
export function signingSetOf<T extends SetMemberDocument>(anchor: T, documents: T[], phone = anchor.signers[0]?.phone): T[] {
  if (!anchor.bundleId || !phone) return [anchor];
  const members = documents.filter((doc) => {
    if (doc.id === anchor.id) return true;
    if (doc.bundleId !== anchor.bundleId || doc.archivedAt || doc.status === "draft") return false;
    if (!doc.signers.some((signer) => signer.phone === phone)) return false;
    const apart = Math.abs(timeOf(doc.createdTime) - timeOf(anchor.createdTime));
    return Number.isFinite(apart) && apart <= SIGNATURE_SET_WINDOW_MS;
  });
  if (!members.some((doc) => doc.id === anchor.id)) members.push(anchor);
  return members.sort((a, b) => (timeOf(a.createdTime) || 0) - (timeOf(b.createdTime) || 0));
}

/** What the CLIENT calls a contract: its customer-facing name, else its template's (as every signing message does). */
export const customerTitleOf = (doc: { templateNameSnapshot?: string; templateCustomerFacingNameSnapshot?: string }): string =>
  doc.templateCustomerFacingNameSnapshot?.trim() || doc.templateNameSnapshot?.trim() || "";

/** The questionnaire a message about `doc` leads the client to, if any. */
export type QuestionnaireInMessage = {
  /** Not signed yet: the questionnaire comes right after. Awaiting: the contract is signed, the questionnaire is open. */
  step: "notStarted" | "awaiting";
  listedFor?: ListingListedFor;
  /** The contract that carries it, when it is ANOTHER contract of the set than the one the message was sent from. */
  carrierTitle?: string;
};

/**
 * Whether a signing-link message sent from `doc` — alone, or as its set's one message — leads the client
 * to a questionnaire still to fill: `doc`'s own, else the first contract of its set that carries one.
 * Null when none does (or every one is filled already).
 */
export function questionnaireInMessage<T extends SetMemberDocument>(doc: T, setDocuments: T[] = []): QuestionnaireInMessage | null {
  const candidates = [doc, ...setDocuments.filter((member) => member.id !== doc.id)];
  for (const candidate of candidates) {
    const step = questionnaireStepOf(candidate);
    if (step !== "notStarted" && step !== "awaiting") continue;
    return {
      step,
      listedFor: questionnaireListedForOf(candidate),
      ...(candidate.id !== doc.id ? { carrierTitle: customerTitleOf(candidate) } : {})
    };
  }
  return null;
}
